import { renderInlineCssVars, useDesignTokens, useGraphTokens, } from '@sas/nova-commons/theme';
import { AddRoute, GeoUIAreaAddress, GeoUILocationPin, GeoUIYourLocation, Location, Search, ShapeRoundedSquareFilled, } from '@sas/react-svg';
import React from 'react';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { Icon } from '@sas/nova-commons/components/Icon';
import { useI18n } from '../../../../locale/I18nContext';
import { useFormatLocale } from '@sas/nova-commons/locale/formatting';
import { MapUnits, RoutingMode } from '@sas-dvr/ltjs-commons';
import { isGeographicFeature, isSearchHint, } from '../../../../store/objectSearch/ObjectSearchState';
import { getFeatureTokens, preventColorInversionStyles } from '../../utils/tokens';
import { Item } from '@sas/nova-commons/components/List';
import cx from 'classnames';
import cn from './PanelListItem.css.json';
import './PanelListItem.css';
export const panelItemHeight = 56; // ListItem "double" height + padding
export const PanelListItem = (props) => {
    var _a, _b;
    const dt = useDesignTokens();
    const gt = useGraphTokens();
    const localize = useI18n();
    const formatLocale = useFormatLocale();
    const description = (_a = props.description) !== null && _a !== void 0 ? _a : getItemDescription(props.item, localize, formatLocale);
    const icon = (_b = props.icon) !== null && _b !== void 0 ? _b : getItemIcon(props.item);
    const hasCounter = typeof props.counter === 'number';
    return (React.createElement(Flow, { style: props.style, className: cx(cn.container, props.className), spacing: 's', orientation: "horizontal" },
        hasCounter && icon ? (React.createElement("div", { title: `(${props.counter}) ${props.item.location}`, className: cn.itemCounterContainer, style: renderInlineCssVars(getItemCounterTokens(dt, gt), 'panelListItem') },
            React.createElement(Icon, { icon: icon, className: cn.iconLarge, theme: (dt) => {
                    return {
                        '--icon-default-override-fg': getItemCounterTokens(dt, gt)['itemCounter-bg'],
                    };
                } }),
            React.createElement("div", { className: cn.itemCounter }, `${props.counter}`))) : null,
        React.createElement(Item, { className: cx(cn.item, !description && cn.boldOverride), title: props.item.name, description: description, group: props.group, icon: icon && !hasCounter
                ? {
                    icon,
                    className: isSearchHint(props.item) ? cn.hintIcon : cn.iconLarge,
                    style: isGeographicFeature(props.item)
                        ? preventColorInversionStyles(props.item, props.item.color)
                        : undefined,
                    tokens: () => isGeographicFeature(props.item) ? getFeatureTokens(dt, props.item) : {},
                }
                : null })));
};
/**
 * Returns the description for an item.
 * Exported so parent components can populate ListProps.getItemInfo.
 */
export function getItemDescription(item, localize, formatLocale) {
    var _a, _b;
    switch (item.type) {
        case 'hint':
        case 'pin':
        case 'result':
        case 'myLocationPin':
            return item.location;
        case 'route':
            if (!item.isRendered) {
                return localize({ key: 'PinPanel.notDrawn.txt' });
            }
            return !!item.routeTravelDuration
                ? `${item.routeTravelDuration}, ${item.routeTravelDistance}`
                : (_a = item.routeTravelDistance) !== null && _a !== void 0 ? _a : undefined;
        case 'area':
            if (!item.isRendered) {
                return localize({ key: 'PinPanel.notDrawn.txt' });
            }
            const formatValue = (value) => value.toLocaleString(formatLocale, { maximumFractionDigits: 3 }) || '';
            const isTimeTravel = item.mode === RoutingMode.DRIVING_TIME ||
                item.mode === RoutingMode.TRUCKING_TIME ||
                item.mode === RoutingMode.WALKING_TIME;
            const amount = ((_b = item.amount) !== null && _b !== void 0 ? _b : []).map(formatValue);
            switch (amount.length) {
                case 1:
                    return localize({
                        key: isTimeTravel
                            ? `PinPanel.description.minutes.1.fmt`
                            : item.unit === MapUnits.MILE
                                ? `PinPanel.description.miles.1.fmt`
                                : `PinPanel.description.kilometers.1.fmt`,
                        args: { value1: amount[0] },
                    });
                case 2:
                    return localize({
                        key: isTimeTravel
                            ? `PinPanel.description.minutes.2.fmt`
                            : item.unit === MapUnits.MILE
                                ? `PinPanel.description.miles.2.fmt`
                                : `PinPanel.description.kilometers.2.fmt`,
                        args: { value1: amount[0], value2: amount[1] },
                    });
                case 3:
                    return localize({
                        key: isTimeTravel
                            ? `PinPanel.description.minutes.3.fmt`
                            : item.unit === MapUnits.MILE
                                ? `PinPanel.description.miles.3.fmt`
                                : `PinPanel.description.kilometers.3.fmt`,
                        args: { value1: amount[0], value2: amount[1], value3: amount[2] },
                    });
            }
    }
}
function getItemIcon(item) {
    switch (item.type) {
        case 'pin':
            return GeoUILocationPin;
        case 'route':
            return AddRoute;
        case 'area':
            return GeoUIAreaAddress;
        case 'hint':
            return item.isMultiple === true ? Search : Location;
        case 'myLocationPin':
            return GeoUIYourLocation;
        case 'result':
            return ShapeRoundedSquareFilled;
        default:
            return null;
    }
}
function getItemCounterTokens(dt, gt) {
    switch (dt.themeVariant) {
        case 'high-contrast':
            return {
                'itemCounter-fg': '#000000',
                'itemCounter-bg': gt.graphDefault.n2,
            };
        case 'dark':
            return {
                'itemCounter-fg': gt.graphDefault.n_5,
                'itemCounter-bg': gt.graphDefault.n,
            };
        default:
            return {
                'itemCounter-fg': gt.graphDefault.white,
                'itemCounter-bg': gt.graphDefault.n_1,
            };
    }
}
